import { Box, Button, Flex } from '@everlywell/ui-kit';
import DynamicIcon from 'components/DynamicIcon';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import SectionHeader, {
  SectionHeaderProps,
} from 'components/PageSections/components/SectionHeader';
import VideoComponent, { VideoComponentProps } from 'components/VideoComponent';
import YouTubeModal from 'components/YouTubeModal';
import React, { useEffect, useState } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';

export type HeroWithExternalVideoProps = {
  title?: string | null;
  titleColor?: string | null;
  titleTag?: SectionHeaderProps['headingLevel'];
  eyebrow?: string | null;
  eyebrowColor?: string | null;
  cta?: Queries.Maybe<Queries.ContentfulCallToAction001>;
  mobileVideo?: VideoComponentProps | null;
  desktopVideo?: VideoComponentProps | null;
  mobileImage?: string;
  desktopImage?: string;
  mobileGradientBackground?: string | null;
  desktopGradientBackground?: string | null;
  videoBackgroundColor?: string | null;
};

export default function HeroWithExternalVideo({
  title,
  titleColor,
  titleTag,
  eyebrow,
  eyebrowColor,
  cta,
  mobileVideo,
  desktopVideo,
  mobileImage,
  desktopImage,
  mobileGradientBackground = null,
  desktopGradientBackground = null,
  videoBackgroundColor = null,
}: HeroWithExternalVideoProps) {
  const [shouldPlayVideo, setShouldPlayVideo] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setShouldPlayVideo(true);
  }, []);

  const trackModalState = (isOpen: boolean) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title || 'No title provided',
        contentfulSection: 'BrandMomentBanner',
        ctaText: cta?.label || 'No CTA label provided',
        modalState: isOpen ? 'opened' : 'closed',
      },
    });
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    trackModalState(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    trackModalState(false);
  };

  return (
    <SectionContainer size={['sm', null, 'lg', 'xl']} marginTop="4">
      <Box
        position="relative"
        borderRadius="3xl"
        overflow="hidden"
        height="100%"
      >
        {/* Video backgrounds - positioned at the very bottom layer */}
        {mobileVideo && (
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            zIndex="0"
            display={['flex', null, 'none']}
            backgroundColor={videoBackgroundColor ?? ''}
          >
            <VideoComponent
              {...mobileVideo}
              loop
              width="100%"
              height="100%"
              objectFit="cover"
              autoPlay={shouldPlayVideo}
              {...(mobileImage ? { poster: mobileImage } : {})}
            />
          </Box>
        )}
        {desktopVideo && (
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            zIndex="0"
            display={['none', null, 'flex']}
            backgroundColor={videoBackgroundColor ?? ''}
          >
            <VideoComponent
              {...desktopVideo}
              loop
              width="100%"
              height="100%"
              objectFit="cover"
              autoPlay={shouldPlayVideo}
              {...(desktopImage ? { poster: desktopImage } : {})}
            />
          </Box>
        )}

        {/* Gradient background and content - positioned above the videos */}
        <Flex
          paddingLeft={[4, null, '56px']}
          paddingRight={[4, null, '56px']}
          paddingTop={[8, null, 20]}
          paddingBottom={[8, null, 20]}
          background={[
            mobileGradientBackground,
            null,
            desktopGradientBackground,
          ]}
          position="relative"
          zIndex="1"
          minHeight={['600px', null, '620px']}
          height="100%"
          flexDirection="column"
        >
          <Flex
            position="relative"
            height="100%"
            flexDirection="column"
            flex="1"
          >
            <Flex
              maxWidth={[null, null, '480px']}
              flexDirection="column"
              alignItems={['center', null, 'flex-start']}
              justifyContent={[null, null, 'space-between']}
              flex="1"
              sx={{
                p: {
                  width: '100%',
                },
              }}
            >
              <Box>
                <SectionHeader
                  title={title}
                  eyebrow={eyebrow}
                  headerAlignment={['center', null, 'left']}
                  eyebrowAlignment={['center', null, 'left']}
                  titleColor={titleColor || ''}
                  eyebrowColor={eyebrowColor || ''}
                  headingLevel={titleTag || 'h1'}
                />
              </Box>

              <Box>
                {cta && cta.label ? (
                  <Button colorScheme="whiteCS" onClick={handleOpenModal}>
                    <Flex justifyContent="center" gap={[1, null, 2]}>
                      {cta?.label}
                      <DynamicIcon
                        icon={cta?.metadata?.context?.ctaParts?.icon}
                        color="tints.black"
                        weight="fill"
                      />
                    </Flex>
                  </Button>
                ) : null}
              </Box>
            </Flex>
          </Flex>
        </Flex>
      </Box>

      {cta?.url ? (
        <YouTubeModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          videoUrl={cta?.url}
        />
      ) : null}
    </SectionContainer>
  );
}
